import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Directive, Input, Pipe, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpUrlEncodingCodec, HttpHeaders, HttpParams, HttpEventType, HttpResponse, HttpRequest, HTTP_INTERCEPTORS, HttpClientModule, HttpXhrBackend, HttpBackend, HttpContextToken } from '@angular/common/http';
import * as i2 from '@angular/platform-browser';
import { Queue } from '@firestitch/common';
import { Observable, of, merge, throwError } from 'rxjs';
import { map, switchMap, filter, tap, catchError } from 'rxjs/operators';
import { isDate, isValid, format, differenceInMinutes } from 'date-fns';
import { parse, parseLocal } from '@firestitch/date';

var ResponseType;
(function (ResponseType) {
    ResponseType["Json"] = "json";
    ResponseType["Text"] = "text";
    ResponseType["ArrayBuffer"] = "arraybuffer";
    ResponseType["Blob"] = "blob";
})(ResponseType || (ResponseType = {}));

var StreamEventType;
(function (StreamEventType) {
    StreamEventType["Data"] = "data";
    StreamEventType["HttpResponse"] = "httpResponse";
})(StreamEventType || (StreamEventType = {}));

class FsApiFile {
    constructor(api, url, filename) {
        this._url = url;
        this._api = api;
        this._name = filename;
        if (!this._name) {
            this._name = (url || '').replace(/\?.*/, '');
            this._name = this._name.substring(this._name.lastIndexOf('/') + 1);
        }
    }
    get name() {
        return this._name;
    }
    get blob() {
        return this._api
            .get(this._url, {}, { handlers: false, responseType: ResponseType.Blob });
    }
    get file() {
        return this._api
            .get(this._url, {}, {
            handlers: false,
            responseType: ResponseType.Blob,
            mapHttpResponseBody: false,
        })
            .pipe(map((event) => {
            let filename = (event.headers.getAll('Content-Disposition') || [])
                .reduce((accum, item) => {
                const matches = item.match(/filename="([^"]+)"/);
                return matches ? matches[1] : accum;
            }, '');
            if (!filename) {
                const url = new URL(event.url);
                filename = url.pathname.split('/').pop();
            }
            return new File([event.body], filename);
        }));
    }
    get blobUrl() {
        return this.blob
            .pipe(map((blob) => URL.createObjectURL(blob)));
    }
    get base64() {
        return this.blob
            .pipe(switchMap((blob) => new Observable((observer) => {
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = () => {
                observer.next(reader.result);
                observer.complete();
            };
            reader.onerror = (error) => observer.error(error);
        })));
    }
    get safeBase64Url() {
        return this.base64
            .pipe(map((data) => this._api.sanitizer.bypassSecurityTrustUrl(data)));
    }
    get safeBase64ResourceUrl() {
        return this.base64
            .pipe(map((data) => this._api.sanitizer.bypassSecurityTrustResourceUrl(data)));
    }
    get safeDataUrl() {
        return this.blob
            .pipe(map((blob) => URL.createObjectURL(blob)), map((data) => this._api.sanitizer.bypassSecurityTrustUrl(data)));
    }
    download(name) {
        this.file
            .subscribe((file) => {
            const a = document.createElement('a');
            document.body.appendChild(a);
            a.style.display = 'none';
            a.href = URL.createObjectURL(file);
            name = name ? name : file.name;
            if (name) {
                a.download = name;
            }
            a.click();
        });
    }
}

class CustomParamsEncoder extends HttpUrlEncodingCodec {
    encodeKey(key) {
        return encodeURIComponent(key);
    }
    encodeValue(value) {
        return encodeURIComponent(value);
    }
    decodeKey(key) {
        return decodeURIComponent(key);
    }
    decodeValue(value) {
        return decodeURIComponent(value);
    }
}

class FsApiConfig {
    constructor(_config, _method, _data) {
        this._config = _config;
        this._method = _method;
        this._data = _data;
        /** A key value store for the request headers. */
        this.headers = {};
        this.encoding = 'json';
        this.interceptors = true;
        this.handlers = true;
        this.key = null;
        this.query = {};
        this.reportProgress = false;
        this.responseType = ResponseType.Json;
        Object.assign(this, _config || {});
        if (this.methodGet) {
            this.query = this._sanitize(_data);
        }
        else {
            this.data = this._sanitize(_data);
        }
        if (this.stream) {
            this.reportProgress = true;
            this.responseType = ResponseType.Text;
        }
    }
    get method() {
        return this._method.toUpperCase();
    }
    get methodGet() {
        return this.method === 'GET';
    }
    /** Adds or overrides a header value based on the name */
    appendHeader(name, value) {
        this.headers[name] = value;
    }
    /**
     * Sanitize the passed object
     *
     * @param obj
     */
    _sanitize(obj, data = {}) {
        if (obj === null || typeof obj !== 'object') {
            return obj;
        }
        Object.keys(obj)
            .forEach((key) => {
            const value = obj[key];
            if (value !== undefined) {
                if (isDate(value)) {
                    if (isValid(value)) {
                        data[key] = format(value, 'yyyy-MM-dd\'T\'HH:mm:ssxxx');
                    }
                }
                else if (Array.isArray(value)) {
                    data[key] = [
                        ...value,
                    ];
                    this._sanitize(value, data[key]);
                }
                else if (value instanceof Blob) {
                    data[key] = value;
                }
                else if (value instanceof Object) {
                    data[key] = {
                        ...value,
                    };
                    this._sanitize(value, data[key]);
                }
                else {
                    data[key] = value;
                }
            }
        });
        return data;
    }
}

class RequestHandler {
    constructor(next, interceptor) {
        this.next = next;
        this.interceptor = interceptor;
    }
    handle(req) {
        return this.interceptor.intercept(req, this.next);
    }
}

class ApiCache {
    constructor() {
        this._cache = Array();
    }
    get(name, params) {
        const cache = this._cache[name];
        if (!cache) {
            return null;
        }
        const paramHash = this._paramHash(params);
        const data = cache[paramHash];
        if (data) {
            if (differenceInMinutes(new Date(), data.date.getTime()) > 30) {
                delete this._cache[name][paramHash];
            }
            return data.data;
        }
        return null;
    }
    set(name, params, data) {
        if (!this._cache[name]) {
            this._cache[name] = [];
        }
        this._cache[name][this._paramHash(params)] = { data: data, date: new Date() };
    }
    clear(name) {
        Object.keys(this._cache).forEach(key => {
            if (name.match(new RegExp(`^${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`))) {
                delete this._cache[key];
            }
        });
    }
    _paramHash(params) {
        return JSON.stringify(params);
    }
}

const FS_API_REQUEST_INTERCEPTOR = new InjectionToken('fs-api.request_interceptor');
const FS_API_RESPONSE_HANDLER = new InjectionToken('fs-api.response_handler');
const FS_API_RESPONSE_DATA_HANDLER = new InjectionToken('fs-api.response_data_handler');
const FS_API_CONFIG = new InjectionToken('fs-api.config');

class FsApiCacheHandler {
    constructor(cache) {
        this._cache = cache;
    }
    success(response, config, request) {
        if (request.method === 'GET') {
            if (config.cache && (request.responseType === 'json' || request.responseType === 'text')) {
                this._cache.set(request.url, config.query, response.body);
            }
        }
        else {
            this._cache.clear(request.url);
        }
    }
    error(error, config) {
        //
    }
    complete(config) {
        //
    }
}

class FsApiResponseBodyHandler {
    success(event, config) {
        this.processData(event.body?.data);
    }
    error(error, config) {
        //
    }
    complete(config) {
        //
    }
    processData(data) {
        if (data instanceof Object) {
            for (const key of Object.keys(data)) {
                data[key] = this.processData(data[key]);
            }
        }
        else {
            if (typeof data === 'string') {
                const match = data.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2}|\.\d{3}Z)?$/);
                if (match) {
                    data = match[1] ? parse(data) : parseLocal(data);
                }
            }
        }
        return data;
    }
}

class FsApiResponseHandler {
    success(event, config) {
        event.body = event.body.data;
        if (config.key) {
            event.body = event.body[config.key];
        }
    }
    error(error, config) {
        //
    }
    complete(config) {
        //
    }
}

class RequestInterceptor {
    constructor(_config, _data) {
        this._config = _config;
        this._data = _data;
    }
    intercept(req, next) {
        return next.handle(req);
    }
}

function objectToFormData(target, formData = new FormData(), namespace = null, level = 0) {
    level++;
    // Depth limit
    if (level > 10) {
        throw Error('Maximum call stack size exceeded');
    }
    if (target === void 0 || target === null) {
        return formData;
    }
    Object.keys(target).forEach((property) => {
        const item = target[property];
        const formKey = namespace ? `${namespace}[${property}]` : property;
        const isBlob = item instanceof Blob;
        if (item && typeof item === 'object' && !isBlob) {
            objectToFormData(item, formData, formKey, level);
        }
        else {
            if (isBlob && item && item.name) {
                formData.append(formKey, item, item.name);
            }
            else {
                formData.append(formKey, item);
            }
        }
    });
    return formData;
}

class BodyHandlerInterceptor extends RequestInterceptor {
    constructor(_config, _data) {
        super(_config, _data);
        this._config = _config;
        this._data = _data;
    }
    intercept(req, next) {
        let body = null;
        switch (this._config.encoding) {
            case 'url':
                {
                    body = this._data;
                }
                break;
            case 'json':
                {
                    body = JSON.stringify(this._data);
                }
                break;
            case 'formdata':
                {
                    body = objectToFormData(this._data);
                }
                break;
        }
        return next.handle(req.clone({ body }));
    }
}

function lookupBlob(data, level = 0) {
    level++;
    // Depth limit
    if (level > 10) {
        throw Error('Maximum call stack size exceeded');
    }
    if (!data) {
        return false;
    }
    return Object.keys(data).some((key) => {
        const item = data[key];
        if (item instanceof File || item instanceof Blob) {
            return true;
        }
        else if (item instanceof Object || Array.isArray(item)) {
            return lookupBlob(item, level);
        }
    });
}

class HeadersHandlerInterceptor extends RequestInterceptor {
    constructor(_config, _data) {
        super(_config, _data);
        this._config = _config;
        this._data = _data;
    }
    intercept(req, next) {
        let headers = new HttpHeaders();
        Object.keys(this._config.headers)
            .forEach((name) => {
            headers = headers.set(name, this._config.headers[name]);
        });
        if (lookupBlob(this._data)) {
            this._config.encoding = 'formdata';
        }
        switch (this._config.encoding) {
            case 'url':
                {
                    headers = headers.set('Content-Type', 'application/x-www-form-urlencoded');
                }
                break;
            case 'json':
                {
                    headers = headers.set('Content-Type', 'text/json');
                }
                break;
            case 'formdata':
                {
                    headers = headers.delete('Content-Type');
                }
                break;
        }
        const modified = req.clone({ headers });
        return next.handle(modified);
    }
}

class ParamsHandlerInterceptor extends RequestInterceptor {
    constructor(_config, _data) {
        super(_config, _data);
        this._config = _config;
        this._data = _data;
    }
    intercept(req, next) {
        let params = new HttpParams({
            encoder: new CustomParamsEncoder()
        });
        const formData = objectToFormData(this._config.query);
        formData.forEach((value, name) => {
            params = params.append(name, value);
        });
        const modified = req.clone({
            params: params,
            reportProgress: this._config.reportProgress
        });
        return next.handle(modified);
    }
}

class FsApi {
    constructor(_http, _sanitizer, 
    // Custom interceptors
    _config, 
    // Custom interceptors
    _httpInterceptors, 
    // Custom interceptors
    _requestInterceptors, 
    // Other callbacks
    _responseHandler, 
    // Other callbacks
    _responseBodyHandler) {
        this._http = _http;
        this._sanitizer = _sanitizer;
        this._config = _config;
        this._httpInterceptors = _httpInterceptors;
        this._requestInterceptors = _requestInterceptors;
        this._responseHandler = _responseHandler;
        this._responseBodyHandler = _responseBodyHandler;
        this.events = [];
        this._queue = new Queue(5);
        this._cache = new ApiCache();
        this._responseHandlers = [new FsApiResponseHandler()];
        this._responseBodyHandlers = [new FsApiResponseBodyHandler()];
        if (_responseHandler) {
            this._responseHandlers = Array.isArray(_responseHandler) ?
                _responseHandler :
                [_responseHandler];
        }
        if (_responseBodyHandler) {
            this._responseBodyHandlers = Array.isArray(_responseBodyHandler) ?
                _responseBodyHandler :
                [_responseBodyHandler];
        }
        this._queue.setLimit((this._config && this._config.maxFileConnections) || 5);
    }
    createApiFile(url, filename) {
        return new FsApiFile(this, url, filename);
    }
    get queue() {
        return this._queue;
    }
    get cache() {
        return this._cache;
    }
    get sanitizer() {
        return this._sanitizer;
    }
    get(url, query, config) {
        return this.request('GET', url, query, config);
    }
    post(url, data, config) {
        return this.request('POST', url, data, config);
    }
    put(url, data, config) {
        return this.request('PUT', url, data, config);
    }
    delete(url, data, config) {
        return this.request('DELETE', url, data, config);
    }
    stream(method, url, data, requestConfig) {
        const config = new FsApiConfig({
            ...this._config,
            ...requestConfig,
            stream: true,
        }, method, data);
        let idx = 0;
        return this._getInterceptorChain(config, config.data)
            .handle(this._createHttpRequest(config, url))
            .pipe(filter((event) => {
            return event?.type === HttpEventType.DownloadProgress ||
                event?.type === HttpEventType.Response;
        }), switchMap((event) => {
            if (event.type === HttpEventType.DownloadProgress) {
                const partialText = event.partialText;
                const text = partialText.substring(idx).trim();
                const data$ = text.split('\n')
                    .map((item) => {
                    return of({ type: StreamEventType.Data, data: JSON.parse(item) });
                });
                idx = partialText.length - 1;
                return merge(...data$);
            }
            return of({
                ...data,
                type: StreamEventType.HttpResponse,
            });
        }));
    }
    request(method, url, data, requestConfig) {
        const config = new FsApiConfig({ ...this._config, ...requestConfig }, method, data);
        if (config.methodGet) {
            if (config.cache) {
                const cache = this.cache.get(url, config.query);
                if (cache) {
                    return of(cache);
                }
            }
        }
        const request = this._createHttpRequest(config, url);
        const handlers = this._getHandlers(config);
        // Do request and process the answer
        const chainedRequest = this._getInterceptorChain(config, config.data)
            .handle(request)
            .pipe(filter((event) => {
            return config.reportProgress || event instanceof HttpResponse;
        }), tap((event) => {
            if (event.type === HttpEventType.Response) {
                handlers.forEach((handler) => {
                    handler.success(event, config, request);
                });
            }
        }), map((event) => {
            return ((config.mapHttpResponseBody ?? true) && event.type === HttpEventType.Response) ?
                event.body :
                event;
        }), tap(() => {
            handlers.forEach((handler) => {
                handler.complete(config);
            });
        }), catchError((error) => {
            handlers.forEach((handler) => {
                handler.error(error, config);
            });
            return throwError(error);
        }));
        // Depends on encoding will send in queue or raw
        if (config.encoding === 'formdata') {
            if (config.queue) {
                return config.queue.push(chainedRequest);
            }
            return this._queue.push(chainedRequest);
        }
        return chainedRequest;
    }
    _createHttpRequest(config, url) {
        return new HttpRequest(config.method, url, null, {
            responseType: config.responseType,
            context: config.context,
        });
    }
    _getHandlers(config) {
        const handlers = [];
        if (config.stream) {
            return handlers;
        }
        if (config.handlers) {
            handlers.push(...this._responseBodyHandlers);
            handlers.push(...this._responseHandlers);
        }
        handlers.push(new FsApiCacheHandler(this._cache));
        return handlers;
    }
    _getInterceptorChain(config, data) {
        const interceptors = [
            new HeadersHandlerInterceptor(config, data),
            new BodyHandlerInterceptor(config, data),
            new ParamsHandlerInterceptor(config, data),
        ];
        if (config.interceptors) {
            // Add custom interceptors into chain
            if (Array.isArray(this._requestInterceptors)) {
                interceptors.push(...this._requestInterceptors
                    .map((interceptor) => interceptor(config, data)));
            }
            else if (this._requestInterceptors) {
                const interceptor = this._requestInterceptors(config, data);
                interceptors.push(interceptor);
            }
            interceptors.push(...this._httpInterceptors);
        }
        // Executing of interceptors
        return interceptors
            .reduceRight((next, interceptor) => {
            return new RequestHandler(next, interceptor);
        }, this._http);
    }
}
FsApi.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApi, deps: [{ token: i1.HttpXhrBackend }, { token: i2.DomSanitizer }, { token: FS_API_CONFIG, optional: true }, { token: HTTP_INTERCEPTORS, optional: true }, { token: FS_API_REQUEST_INTERCEPTOR, optional: true }, { token: FS_API_RESPONSE_HANDLER, optional: true }, { token: FS_API_RESPONSE_DATA_HANDLER, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
FsApi.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApi });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApi, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpXhrBackend }, { type: i2.DomSanitizer }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_API_CONFIG]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [HTTP_INTERCEPTORS]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_API_REQUEST_INTERCEPTOR]
                }] }, { type: FsApiResponseHandler, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_API_RESPONSE_HANDLER]
                }] }, { type: FsApiResponseBodyHandler, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_API_RESPONSE_DATA_HANDLER]
                }] }]; } });

class FsApiImageDirective {
    constructor(_api, _el) {
        this._api = _api;
        this._el = _el;
        this.display = this._el.nativeElement.style.display;
        this._el.nativeElement.style.display = 'none';
    }
    ngOnInit() {
        const file = this._api.createApiFile(this.url);
        file.blobUrl
            .subscribe((data) => {
            this._el.nativeElement.setAttribute('src', data);
            this._el.nativeElement.style.display = this.display;
        });
    }
}
FsApiImageDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiImageDirective, deps: [{ token: FsApi }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsApiImageDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsApiImageDirective, selector: "[fsApiImage]", inputs: { url: "url" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiImageDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsApiImage]',
                }]
        }], ctorParameters: function () { return [{ type: FsApi }, { type: i0.ElementRef }]; }, propDecorators: { url: [{
                type: Input
            }] } });

class FsApiImagePipe {
    constructor(_api, _sanitizer) {
        this._api = _api;
        this._sanitizer = _sanitizer;
    }
    transform(url) {
        return this._api.createApiFile(url)
            .safeDataUrl;
    }
}
FsApiImagePipe.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiImagePipe, deps: [{ token: FsApi }, { token: i2.DomSanitizer }], target: i0.ɵɵFactoryTarget.Pipe });
FsApiImagePipe.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiImagePipe, name: "fsApiImage" });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiImagePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'fsApiImage',
                }]
        }], ctorParameters: function () { return [{ type: FsApi }, { type: i2.DomSanitizer }]; } });

class FsApiModule {
    static forRoot(config = {}) {
        return {
            ngModule: FsApiModule,
            providers: [
                { provide: FS_API_CONFIG, useValue: config },
                FsApi,
            ],
        };
    }
}
FsApiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsApiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiModule, declarations: [FsApiImageDirective,
        FsApiImagePipe], imports: [CommonModule,
        HttpClientModule], exports: [FsApiImageDirective,
        FsApiImagePipe] });
FsApiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiModule, providers: [
        FsApi,
        HttpXhrBackend,
        { provide: HttpBackend, useExisting: HttpXhrBackend },
    ], imports: [[
            CommonModule,
            HttpClientModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsApiModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        HttpClientModule,
                    ],
                    declarations: [
                        FsApiImageDirective,
                        FsApiImagePipe,
                    ],
                    providers: [
                        FsApi,
                        HttpXhrBackend,
                        { provide: HttpBackend, useExisting: HttpXhrBackend },
                    ],
                    exports: [
                        FsApiImageDirective,
                        FsApiImagePipe,
                    ],
                }]
        }] });

function makeInterceptorFactory(klass) {
    return function (...args) {
        return (config, data) => {
            return new klass(config, data, ...args);
        };
    };
}

const DisplayApiError = new HttpContextToken(() => true);

// Modules

/**
 * Generated bundle index. Do not edit.
 */

export { BodyHandlerInterceptor, CustomParamsEncoder, DisplayApiError, FS_API_CONFIG, FS_API_REQUEST_INTERCEPTOR, FS_API_RESPONSE_HANDLER, FsApi, FsApiConfig, FsApiFile, FsApiImageDirective, FsApiImagePipe, FsApiModule, FsApiResponseBodyHandler, FsApiResponseHandler, HeadersHandlerInterceptor, ParamsHandlerInterceptor, RequestHandler, RequestInterceptor, ResponseType, StreamEventType, makeInterceptorFactory };
